import type { FC } from 'react';
import {
  IoMdBusiness,
  IoMdPeople,
  IoMdPhonePortrait,
  IoIosStats,
  IoMdCloudDownload,
} from 'react-icons/io';
import { AiFillQuestionCircle } from 'react-icons/ai';
import { NavLink } from 'react-router-dom';

/**
 * @description Props for the SidebarLink component
 * @typedef {Object} SidebarLinkProps
 * @property {string} title - The text to display for the link
 * @property {string} link - The URL path the link navigates to
 * @property {keyof typeof Icons} icon - The icon key to display (must match a key in the Icons object)
 * @property {boolean} [activeLink] - Optional flag to force the link to appear active
 * @property {string} [testid] - Optional test ID for testing purposes
 */
type SidebarLinkProps = {
  title: string;
  link: string;
  icon: keyof typeof Icons;
  activeLink?: boolean;
  testid?: string;
};

/**
 * @description Collection of icon elements mapped to their identifier keys
 * @type {Object.<string, JSX.Element>}
 */
const Icons = {
  building: <IoMdBusiness size={20} className="nav-sidebar__link-icon" />,
  users: <IoMdPeople size={20} className="nav-sidebar__link-icon" />,
  device: <IoMdPhonePortrait size={20} className="nav-sidebar__link-icon" />,
  reports: <IoIosStats size={20} className="nav-sidebar__link-icon" />,
  deployment: (
    <IoMdCloudDownload size={20} className="nav-sidebar__link-icon" />
  ),
  help: <AiFillQuestionCircle size={20} className="nav-sidebar__link-icon" />,
};

/**
 * @description SidebarLink component renders a navigation link with an icon in the sidebar.
 * Displays visual feedback for active state with border and background styling.
 *
 * @SADtrace SAD-003-1.1, SAD-003-2.1, SAD-003-3.1, SAD-003-4.1, SAD-003-5.1, SAD-003-6.1
 *
 * @History
 * 04-18-2025 aleksandar dukleski: DLA-1364 added function headers
 * 11-05-2025 aleksandar dukleski: DLA-1428 added history tag to function headers
 *
 * @component
 * @param {SidebarLinkProps} props - The component props
 * @param {string} props.title - The text to display for the link
 * @param {string} props.link - The URL path the link navigates to
 * @param {keyof typeof Icons} props.icon - The icon key to display
 * @param {boolean} [props.activeLink] - Optional flag to force the link to appear active
 * @param {string} [props.testid] - Optional test ID for testing purposes
 *
 * @returns {JSX.Element} A styled navigation link with an icon
 */
const SidebarLink: FC<SidebarLinkProps> = ({
  title,
  link,
  icon,
  activeLink,
  testid,
}) => {
  return (
    <NavLink
      style={({ isActive }) => ({
        borderLeft:
          isActive || activeLink
            ? '4px solid #0895DF'
            : '4px solid transparent',
        background: isActive || activeLink ? '#3A4250' : 'transparent',
      })}
      className="nav-sidebar__link"
      to={link}
      data-testid={testid}
    >
      {Icons[icon]}
      {title}
    </NavLink>
  );
};

export default SidebarLink;
